import { getElement, getElements } from './util';

// Abandon a login which has gone unanswered for this many milliseconds, so that a request which
// hangs (a backend retrieving identity provider metadata of its own, for instance) surfaces an
// error rather than leaving the SSO buttons disabled indefinitely.
const REQUEST_TIMEOUT = 15000;

// Whether a login is already being initiated. Each login is issued a single-use state parameter,
// which the completion view compares against the value recorded in the session, so a second login
// would invalidate the state of the one being navigated to. This is scoped to the document rather
// than to the form: SAML renders one form per identity provider, and all of them write the session
// keys of the same backend.
let loginPending = false;

/**
 * Enable or disable every SSO button on the page. All of them are disabled while a login is being
 * initiated, as only one login can be in flight at a time.
 */
function setButtonsDisabled(disabled: boolean): void {
  for (const form of getElements<HTMLFormElement>('form.sso-login-form')) {
    for (const button of form.querySelectorAll<HTMLButtonElement>('button[type="submit"]')) {
      button.disabled = disabled;
    }
  }
}

function setErrorVisible(visible: boolean): void {
  const error = getElement('sso-error');
  if (error === null) return;

  error.classList.toggle('d-none', !visible);
  if (visible) {
    // Revealing an alert whose text has not itself changed is not reliably announced by a screen
    // reader, so move focus to it (the login the user asked for did not begin, and the reason for
    // that is the only thing worth their attention).
    error.focus();
  }
}

/**
 * Begin an SSO login by navigating to the identity provider, rather than by submitting the form.
 *
 * The social auth "begin" endpoint responds with a redirect to the identity provider. Chromium-based
 * browsers evaluate the CSP `form-action` directive against every hop in a form submission's
 * redirect chain, so a deployment which serves NetBox with `form-action 'self'` blocks that redirect
 * and the SSO button appears to do nothing. Requesting the identity provider's URL and navigating to
 * it here sidesteps the directive, which does not govern a navigation initiated by a script.
 */
async function beginLogin(form: HTMLFormElement): Promise<void> {
  const body = new URLSearchParams();
  for (const [name, value] of new FormData(form).entries()) {
    if (typeof value === 'string') {
      body.append(name, value);
    }
  }

  // Browsers predating AbortSignal.timeout() issue the request without a deadline, which is no
  // worse than the behavior they had before it was imposed.
  const signal =
    typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(REQUEST_TIMEOUT) : null;

  const res = await fetch(form.action, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body,
    credentials: 'same-origin',
    signal,
  });
  if (!res.ok || !(res.headers.get('Content-Type') ?? '').includes('application/json')) {
    throw new Error(`The login request returned an unexpected response (HTTP ${res.status})`);
  }

  const { url, html } = (await res.json()) as { url?: string; html?: string };
  if (typeof url === 'string') {
    window.location.assign(url);
  } else if (typeof html === 'string') {
    // The backend renders its own HTML (an auto-submitting form, for instance) instead of
    // redirecting. That document has already been generated by the request above, so render it in
    // place; submitting the form to fetch it again would initiate the login a second time. Note
    // that this does not evade `form-action`: the document is written into NetBox's own, so the
    // form it carries is submitted under NetBox's policy exactly as it would have been otherwise.
    document.open();
    document.write(html);
    document.close();
  } else {
    throw new Error('The login response contained neither a URL nor a document');
  }
}

export function initSSOForms(): void {
  for (const form of getElements<HTMLFormElement>('form.sso-login-form')) {
    form.addEventListener('submit', event => {
      event.preventDefault();

      if (loginPending) return;
      loginPending = true;
      setErrorVisible(false);
      setButtonsDisabled(true);

      beginLogin(form).catch(error => {
        // Report the failure rather than falling back to submitting the form: a deployment which
        // serves NetBox with `form-action 'self'` — the very condition this indirection exists to
        // work around — blocks that submission silently, leaving a dead button and no explanation.
        // The alert shown to the user cannot say why the login failed, so log the reason.
        console.error(error);
        loginPending = false;
        setButtonsDisabled(false);
        setErrorVisible(true);
      });
    });
  }

  // A browser which restores the login page from its back/forward cache (having navigated to the
  // identity provider and returned) preserves both the DOM and the state above, which would
  // otherwise leave every SSO button permanently disabled. Only a restore may clear the guard:
  // pageshow also fires on an ordinary load, after `load` and so after a login begun in the
  // meantime, where resetting it would readmit the very double-submission it exists to prevent.
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;

    loginPending = false;
    setButtonsDisabled(false);
  });
}
